const OPEN_CLASS = 'is-open';
const VIEWPORT_MARGIN = 16;
const GAP = 10;

/**
 * @param {HTMLElement} popover
 */
const resetPopoverPosition = (popover) => {
	popover.style.position = '';
	popover.style.top = '';
	popover.style.left = '';
	popover.style.right = '';
	popover.style.bottom = '';
	popover.style.zIndex = '';
};

/**
 * @param {HTMLElement} popover
 * @param {HTMLButtonElement} trigger
 */
const positionPopover = (popover, trigger) => {
	const step = trigger.closest('.process__step');
	const opensUp =
		step?.classList.contains('process__step--3') ||
		step?.classList.contains('process__step--4');
	const alignRight = step?.classList.contains('process__step--4');
	const triggerRect = trigger.getBoundingClientRect();
	const popoverWidth = popover.offsetWidth;
	const popoverHeight = popover.offsetHeight;

	let top = opensUp
		? triggerRect.top - popoverHeight - GAP
		: triggerRect.bottom + GAP;
	let left = alignRight ? triggerRect.right - popoverWidth : triggerRect.left;

	left = Math.max(
		VIEWPORT_MARGIN,
		Math.min(left, window.innerWidth - popoverWidth - VIEWPORT_MARGIN),
	);
	top = Math.max(
		VIEWPORT_MARGIN,
		Math.min(top, window.innerHeight - popoverHeight - VIEWPORT_MARGIN),
	);

	popover.style.position = 'fixed';
	popover.style.zIndex = '9999';
	popover.style.top = `${top}px`;
	popover.style.left = `${left}px`;
	popover.style.right = 'auto';
	popover.style.bottom = 'auto';
};

/**
 * @param {HTMLElement} popover
 */
const closePopover = (popover) => {
	popover.hidden = true;
	popover.classList.remove(OPEN_CLASS);
	resetPopoverPosition(popover);

	const trigger = popover
		.closest('.process__read-more-wrap')
		?.querySelector('.js-process-read-more');

	if (trigger instanceof HTMLButtonElement) {
		trigger.setAttribute('aria-expanded', 'false');
	}
};

/**
 * @param {HTMLElement} section
 * @param {HTMLElement} popover
 * @param {HTMLButtonElement} trigger
 */
const openPopover = (section, popover, trigger) => {
	section.querySelectorAll('.js-process-read-more-popover').forEach((item) => {
		if (item !== popover) {
			closePopover(item);
		}
	});

	popover.hidden = false;
	popover.classList.add(OPEN_CLASS);
	trigger.setAttribute('aria-expanded', 'true');

	requestAnimationFrame(() => {
		positionPopover(popover, trigger);
	});
};

/**
 * @param {HTMLElement} section
 */
const bindSection = (section) => {
	section.querySelectorAll('.js-process-read-more').forEach((trigger) => {
		if (!(trigger instanceof HTMLButtonElement)) {
			return;
		}

		const popover = trigger
			.closest('.process__read-more-wrap')
			?.querySelector('.js-process-read-more-popover');

		if (!(popover instanceof HTMLElement)) {
			return;
		}

		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			const isOpen = !popover.hidden;
			if (isOpen) {
				closePopover(popover);
				return;
			}

			openPopover(section, popover, trigger);
		});
	});

	section.querySelectorAll('.js-process-read-more-close').forEach((closeBtn) => {
		closeBtn.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			const popover = closeBtn.closest('.js-process-read-more-popover');
			if (popover instanceof HTMLElement) {
				closePopover(popover);
			}
		});
	});
};

/**
 * @param {NodeListOf<Element>} sections
 */
const repositionOpenPopovers = (sections) => {
	sections.forEach((section) => {
		if (!(section instanceof HTMLElement)) {
			return;
		}

		section.querySelectorAll('.js-process-read-more-popover:not([hidden])').forEach((popover) => {
			if (!(popover instanceof HTMLElement)) {
				return;
			}

			const trigger = popover
				.closest('.process__read-more-wrap')
				?.querySelector('.js-process-read-more');

			if (trigger instanceof HTMLButtonElement) {
				positionPopover(popover, trigger);
			}
		});
	});
};

export const initProcessReadMorePopover = () => {
	const sections = document.querySelectorAll('.js-process-section');
	if (!sections.length) {
		return;
	}

	sections.forEach((section) => {
		if (section instanceof HTMLElement) {
			bindSection(section);
		}
	});

	let resizeFrame = 0;
	window.addEventListener(
		'resize',
		() => {
			cancelAnimationFrame(resizeFrame);
			resizeFrame = requestAnimationFrame(() => repositionOpenPopovers(sections));
		},
		{ passive: true },
	);

	window.addEventListener(
		'scroll',
		() => {
			cancelAnimationFrame(resizeFrame);
			resizeFrame = requestAnimationFrame(() => repositionOpenPopovers(sections));
		},
		{ passive: true },
	);

	document.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Node)) {
			return;
		}

		sections.forEach((section) => {
			if (!(section instanceof HTMLElement)) {
				return;
			}

			section.querySelectorAll('.js-process-read-more-popover:not([hidden])').forEach((popover) => {
				if (!(popover instanceof HTMLElement)) {
					return;
				}

				const wrap = popover.closest('.process__read-more-wrap');
				if (wrap?.contains(target)) {
					return;
				}

				closePopover(popover);
			});
		});
	});

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') {
			return;
		}

		sections.forEach((section) => {
			if (!(section instanceof HTMLElement)) {
				return;
			}

			section.querySelectorAll('.js-process-read-more-popover:not([hidden])').forEach((popover) => {
				if (popover instanceof HTMLElement) {
					closePopover(popover);
				}
			});
		});
	});
};
